import _ from "lodash";

import { EnrollmentPaymentRefundFragmentFragment } from "./generated/graphql";

export const REASONS = {
  // These are also stored in the database, avoid changing.
  TEACHER_MISSED: "teacher missed class",
  LEARNER_MISSED: "learner missed class",
  TEACHER_DISCOUNT: "teacher discount",
  FAMILY_DISSATISFIED: "family is dissatisfied",
  COURTESY_EXCEPTION: "courtesy exception",
  PARENT_DISSATISFIED: "parent is dissatisfied",
  PARENT_DISSATISFIED_TEACHER_CLASS: "teacher or class is not a good fit",
  PARENT_NOT_INTERESTED: "parent is no longer interested",
  PARENT_SKIP: "parent would like to skip 1-2 weeks",
  PARENT_SCHEDULE: "parent has a schedule conflict",
  PARENT_BUSY: "parent is too busy",
  PARENT_CLASS_LEVEL: "class is the wrong level",
  PARENT_BUDGET: "class does not fit in budget",
  PARENT_TRIAL: "parent wanted to trial the class",
  LEARNER_SCHEDULE: "learner has schedule conflict or is no longer interested",
  FAMILY_SCHEDULE: "family has schedule conflict / is no longer interested",
  FAMILY_ACCESS: "family could not access class",
  LEARNER_ACCESS: "learner could not access class",
  TEACHER_CANCELED: "teacher canceled class",
  REPLACED_WITH_PO: "replaced with purchase order (charter team only)",
  OUTSCHOOL_OFFER: "Outschool offer",
  REFERRAL_PROGRAM: "referral program",
  LEARNER_AGE: "learner outside of age range",
  CLUB_INTRO_VIDEO_REJECTED: "admin rejected learner's club introduction video",
  TRUST_AND_SAFETY: "trust & safety",
  EDUCATOR_SCHEDULE: "I have a schedule conflict",
  LEARNER_MISSED_CONSECUTIVE:
    "learner has missed at least 3 consecutive class meetings",
  LEARNER_NO_FINISH: "My learner does not want to finish this class",
  LOW_VALUE: "Do not feel this class is delivering enough value",
  UNRESPONSIVE_TEACHER: "Unresponsive teacher",
  POOR_QUALITY: "Poor audio or video quality",
  OTHER: "other"
};

export function totalCents(
  epRefunds: { amount_cents?: number }[] | null
): number {
  return epRefunds ? _.sumBy(epRefunds, "amount_cents") : 0;
}

export function totalAmount(
  epRefunds: { amount_in_local_currency?: number | null }[] | null
): number {
  return epRefunds ? _.sumBy(epRefunds, "amount_in_local_currency") : 0;
}

export function totalCentsFromTeacherPayout(
  epRefunds: EnrollmentPaymentRefundFragmentFragment[]
): number {
  return epRefunds
    ? epRefunds.reduce(
        (total, refund) =>
          total + (refund.reduces_teacher_payout ? refund.amount_cents : 0),
        0
      )
    : 0;
}

/**
 * Warning!  This enum be kept in sync with
 * lang-js/packages/enrollment-payment-refund/src/index.ts
 */
export enum USER_ROLES {
  OUTSCHOOL = "Outschool",
  TEACHER = "teacher",
  PARENT = "parent",
  SYSTEM = "system"
}

export const ADMIN_REFUND_ADD_OUTSCHOOL_CREDIT_REASON_OPTIONS = [
  "learning experience pledge",
  "cx courtesy",
  "referral program"
];

export const ADMIN_REFUND_STANDARD_REFUND_REASON_OPTIONS = [
  "platform error",
  "refund eligible",
  "teacher refund",
  "referral program",
  "charter refund (charter team only)",
  "replaced with purchase order (charter team only)",
  "trust & safety",
  "admin rejected learner's club introduction video"
];

export const ADMIN_REFUND_STANDARD_REFUND_FROM_TEACHER_PAYOUT_OPTIONS = [
  "refund eligible",
  "teacher refund",
  "charter refund (charter team only)",
  "replaced with purchase order (charter team only)",
  "trust & safety",
  "admin rejected learner's club introduction video"
];

export const ADMIN_REFUND_REASON_OPTIONS =
  ADMIN_REFUND_ADD_OUTSCHOOL_CREDIT_REASON_OPTIONS.concat(
    ADMIN_REFUND_STANDARD_REFUND_REASON_OPTIONS
  );

export const ADMIN_WITHDRAW_REASON_OPTIONS = ADMIN_REFUND_REASON_OPTIONS;

export const TEACHER_REFUND_REASON_OPTIONS = [
  REASONS.TEACHER_DISCOUNT,
  REASONS.TEACHER_CANCELED,
  REASONS.TEACHER_MISSED,
  REASONS.LEARNER_MISSED,
  REASONS.PARENT_NOT_INTERESTED,
  REASONS.PARENT_SCHEDULE,
  REASONS.PARENT_DISSATISFIED,
  REASONS.OTHER
];

export const TEACHER_WITHDRAW_REASON_OPTIONS = [
  // No discounts
  REASONS.TEACHER_CANCELED,
  REASONS.TEACHER_MISSED,
  REASONS.LEARNER_MISSED,
  REASONS.LEARNER_AGE,
  REASONS.PARENT_NOT_INTERESTED,
  REASONS.PARENT_SCHEDULE,
  REASONS.PARENT_DISSATISFIED,
  REASONS.OTHER
];

export const PARENT_REFUND_REASON_OPTIONS = [
  REASONS.PARENT_DISSATISFIED_TEACHER_CLASS,
  REASONS.PARENT_SCHEDULE,
  REASONS.PARENT_SKIP,
  REASONS.PARENT_BUSY,
  REASONS.PARENT_CLASS_LEVEL,
  REASONS.PARENT_NOT_INTERESTED,
  REASONS.PARENT_BUDGET,
  REASONS.PARENT_TRIAL,
  REASONS.OTHER
];

export const TEACHER_ONE_ON_ONE_CANCEL_REASON_OPTIONS = [
  REASONS.EDUCATOR_SCHEDULE,
  REASONS.LEARNER_MISSED_CONSECUTIVE,
  REASONS.LEARNER_AGE,
  REASONS.PARENT_NOT_INTERESTED,
  REASONS.PARENT_SCHEDULE,
  REASONS.PARENT_DISSATISFIED,
  REASONS.OTHER
];
